import { useQuery } from '@tanstack/react-query';
import {
  CreditCard, CheckCircle, AlertTriangle, TrendingUp, RefreshCw,
} from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { subscriptionService, type SubscriptionAuditLog } from '@/services/subscription.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { StatCard } from '@/components/ui/stat-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DueDateBadge } from './components/DueDateBadge';
import { SubscriptionStatusBadge } from './components/SubscriptionStatusBadge';
import { format } from 'date-fns';

function fmt(v: string | null | undefined): string {
  if (!v) return '—';
  return format(new Date(v), 'dd MMM yyyy');
}

function OverviewSkeleton() {
  return (
    <div className="space-y-5">
      <div className="h-8 w-56 rounded-lg bg-muted/60 animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl bg-card p-5 shadow-card animate-pulse">
            <div className="h-4 w-24 rounded bg-muted/60 mb-3" />
            <div className="h-8 w-16 rounded bg-muted/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

const ACTION_COLORS: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-teal-100 text-teal-700',
  renewed: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-red-100 text-red-700',
  paused: 'bg-amber-100 text-amber-700',
  resumed: 'bg-teal-100 text-teal-700',
  expired: 'bg-slate-100 text-slate-700',
};

export function SubscriptionOverviewPage() {
  const statsQuery = useQuery({
    queryKey: ['subscriptions', 'stats'],
    queryFn: () => subscriptionService.getStats(),
  });

  if (statsQuery.isLoading) return <OverviewSkeleton />;

  if (statsQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
        <p className="text-sm text-destructive">Failed to load subscription overview.</p>
        <button
          onClick={() => statsQuery.refetch()}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const rawStats = statsQuery.data!;
  const stats = {
    ...rawStats,
    by_status: rawStats.by_status ?? [],
    cost_by_category: rawStats.cost_by_category ?? [],
    expiring_soon: rawStats.expiring_soon ?? [],
    upcoming_renewals: rawStats.upcoming_renewals ?? [],
    recent_activity: rawStats.recent_activity ?? [],
  };
  const maxCost = Math.max(1, ...stats.cost_by_category.map((c) => c.total));
  const totalByStatus = stats.by_status.reduce((acc, s) => acc + s.cnt, 0) || 1;

  const upcomingColumns = [
    { key: 'name', header: 'Name', cell: (r: typeof stats.upcoming_renewals[0]) => <span className="font-medium text-sm">{r.name}</span> },
    { key: 'cycle', header: 'Cycle', cell: (r: typeof stats.upcoming_renewals[0]) => <span className="text-sm">{r.billing_cycle_name}</span> },
    { key: 'due', header: 'Next Due', cell: (r: typeof stats.upcoming_renewals[0]) => <DueDateBadge nextDueDate={r.next_due_date} /> },
    {
      key: 'amount', header: 'Amount',
      cell: (r: typeof stats.upcoming_renewals[0]) => (
        <span className="font-medium text-sm">{r.currency_symbol}{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
      ),
    },
    { key: 'status', header: 'Status', cell: (r: typeof stats.upcoming_renewals[0]) => <SubscriptionStatusBadge name={r.status_name} color={r.status_color} /> },
  ];

  const activityColumns: Column<SubscriptionAuditLog>[] = [
    {
      key: 'action', header: 'Action',
      cell: (r) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${ACTION_COLORS[r.action] ?? 'bg-muted text-muted-foreground'}`}>
          {r.action}
        </span>
      ),
    },
    {
      key: 'subscription', header: 'Subscription',
      cell: (r) => r.subscription_name ?? <span className="text-muted-foreground text-sm">—</span>,
    },
    {
      key: 'by', header: 'By',
      cell: (r) => r.actor_first
        ? <span className="text-sm">{`${r.actor_first} ${r.actor_last ?? ''}`.trim()}</span>
        : <span className="text-muted-foreground text-sm">System</span>,
    },
    {
      key: 'date', header: 'Date',
      cell: (r) => <span className="text-xs text-muted-foreground">{fmt(r.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold text-foreground">Subscriptions Overview</h1>

      <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_OVERVIEW}>
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total Subscriptions"
              value={stats.total_subscriptions}
              tone="sky"
              icon={CreditCard}
            />
            <StatCard
              label="Active"
              value={stats.active_subscriptions}
              tone="emerald"
              icon={CheckCircle}
            />
            <StatCard
              label="Expiring Soon"
              value={stats.expiring_soon_count}
              tone="amber"
              icon={AlertTriangle}
            />
            <StatCard
              label="Monthly Cost"
              value={`₹${Number(stats.monthly_cost).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              tone="violet"
              icon={TrendingUp}
            />
          </div>

          {/* Expiring soon banner */}
          {stats.expiring_soon_count > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">{stats.expiring_soon_count} subscription{stats.expiring_soon_count !== 1 ? 's' : ''} expiring soon</p>
                <ul className="mt-1 space-y-0.5">
                  {stats.expiring_soon.slice(0, 5).map((s) => (
                    <li key={s.id} className="text-amber-700">
                      {s.name} — <span className="font-medium">{fmt(s.next_due_date)}</span>
                    </li>
                  ))}
                  {stats.expiring_soon.length > 5 && (
                    <li className="text-amber-600 text-xs">+{stats.expiring_soon.length - 5} more</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* Middle grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Status */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Subscriptions by Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {stats.by_status.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No data</p>
                ) : (
                  stats.by_status.map((s) => (
                    <div key={s.name} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          <span className="text-sm">{s.name}</span>
                        </div>
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {s.cnt}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.round((s.cnt / totalByStatus) * 100)}%`, backgroundColor: s.color }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Cost by Category */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Monthly Cost by Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats.cost_by_category.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No data</p>
                ) : (
                  stats.cost_by_category.map((c) => (
                    <div key={c.name} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm truncate">{c.name}</span>
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ml-2 shrink-0">
                          ₹{Number(c.total).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary/60 transition-all"
                          style={{ width: `${Math.round((c.total / maxCost) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Renewals */}
          <div>
            <h2 className="text-base font-semibold mb-3">Upcoming Renewals</h2>
            <DataTable
              columns={upcomingColumns as Column<typeof stats.upcoming_renewals[0]>[]}
              rows={stats.upcoming_renewals}
              rowKey={(r) => r.id}
              loading={statsQuery.isLoading}
              empty="No upcoming renewals."
            />
          </div>

          {/* Recent Activity */}
          <div>
            <h2 className="text-base font-semibold mb-3">Recent Activity</h2>
            <DataTable
              columns={activityColumns}
              rows={stats.recent_activity}
              rowKey={(r) => r.id}
              loading={statsQuery.isLoading}
              empty="No recent activity."
            />
          </div>
        </>
      </PermissionGate>
    </div>
  );
}
